import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useDispatch, useSelector } from "react-redux";
import { setSportFilter } from "../store/filters/filtersReducer";
import "../assets/css/sport-switcher.css";

const Url = process.env.REACT_APP_MAIN_URL;

/**
 * Global sport filter for admins.
 * Options come from the Sports table (Basic Tables → Sport), so a new sport
 * appears here as soon as it is added and marked ACTIVE.
 * Up to 3 sports render as a segmented control; more switch to a dropdown.
 *
 * Sports are fetched locally (not through the sport reducer) so this control
 * never overwrites the paged list on the All Sports page.
 */
const SportSwitcher = () => {
  const dispatch = useDispatch();
  const { token } = useSelector((state) => state.user);
  const { sportId } = useSelector((state) => state.filters || {});
  const [allSports, setAllSports] = useState([]);

  useEffect(() => {
    if (!token) return undefined;
    let cancelled = false;
    axios
      .get(`${Url}api/sports`, {
        params: { page: 0, size: 100 },
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((res) => {
        if (!cancelled) setAllSports(Array.isArray(res.data?.data) ? res.data.data : []);
      })
      .catch(() => {
        if (!cancelled) setAllSports([]);
      });
    return () => {
      cancelled = true;
    };
  }, [token]);

  const sports = useMemo(
    () =>
      allSports
        .filter((s) => !s.sportStatus || s.sportStatus === "ACTIVE")
        .sort((a, b) => (a.name || "").localeCompare(b.name || "")),
    [allSports]
  );

  // If the saved sport was deleted or deactivated, fall back to All sports.
  useEffect(() => {
    if (sportId && sports.length > 0 && !sports.some((s) => s.sportId === sportId)) {
      dispatch(setSportFilter(null, null));
    }
  }, [sportId, sports, dispatch]);

  const choose = (id) => {
    const sport = sports.find((s) => s.sportId === id);
    dispatch(setSportFilter(id, sport?.name ?? null));
  };

  if (sports.length === 0) return null;

  if (sports.length > 3) {
    return (
      <label className="sport-switcher-select">
        <span className="visually-hidden">Sport</span>
        <select
          value={sportId ?? ""}
          onChange={(e) => choose(e.target.value ? Number(e.target.value) : null)}
        >
          <option value="">All sports</option>
          {sports.map((s) => (
            <option key={s.sportId} value={s.sportId}>
              {s.name}
            </option>
          ))}
        </select>
      </label>
    );
  }

  const options = [{ sportId: null, name: "All sports" }, ...sports];
  return (
    <div className="sport-switcher" role="group" aria-label="Sport">
      {options.map((s) => {
        const active = (sportId ?? null) === s.sportId;
        return (
          <button
            key={s.sportId ?? "all"}
            type="button"
            className={`sport-switcher-btn${active ? " is-active" : ""}`}
            aria-pressed={active}
            onClick={() => choose(s.sportId)}
          >
            {s.name}
          </button>
        );
      })}
    </div>
  );
};

export default SportSwitcher;
